/**
 * AdminGeoBlocking Component
 * Manage geo-blocking configuration and view statistics
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import useAdminAuth from '../../hooks/useAdminAuth';
import { geoBlockingApi, type GeoBlockingConfig, type GeoBlockingStats, type IPTestResult } from '../../services/adminService';

const countryNames: Record<string, string> = {
  'US': 'United States',
  'GB': 'United Kingdom',
  'CA': 'Canada',
  'AU': 'Australia',
  'CN': 'China',
  'RU': 'Russia',
  'KP': 'North Korea',
  'IR': 'Iran',
  'DE': 'Germany',
  'FR': 'France',
  'JP': 'Japan',
  'IN': 'India',
  'BR': 'Brazil',
  'MX': 'Mexico'
};

const AdminGeoBlocking: React.FC = () => {
  const { isAdmin } = useAdminAuth();
  const [config, setConfig] = useState<GeoBlockingConfig | null>(null);
  const [stats, setStats] = useState<GeoBlockingStats | null>(null);
  const [testIP, setTestIP] = useState('');
  const [testResult, setTestResult] = useState<IPTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load config and stats independently so a stats failure doesn't block the page
      const [configRes, statsRes] = await Promise.allSettled([
        geoBlockingApi.getConfig(),
        geoBlockingApi.getStats()
      ]);

      if (configRes.status === 'fulfilled') {
        setConfig(configRes.value.config);
      } else {
        console.error('Failed to load geo-blocking config:', configRes.reason);
        setError('Failed to load geo-blocking configuration');
      }

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.stats);
      } else {
        console.warn('Failed to load geo-blocking stats:', statsRes.reason);
        // Set empty stats so the page still renders
        setStats({ totalBlocked: 0, totalAllowed: 0, blockedByCountry: {}, recentBlocks: [] });
      }
    } catch (err) {
      console.error('Failed to load geo-blocking data:', err);
      setError('Failed to load geo-blocking data');
    } finally {
      setLoading(false);
    }
  };

  const handleTestIP = async () => {
    if (!testIP.trim()) return;

    try {
      setTesting(true);
      const result = await geoBlockingApi.testIP(testIP.trim());
      setTestResult(result.result);
    } catch (err) {
      console.error('IP test failed:', err);
      setError('Failed to test IP address');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading geo-blocking configuration...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Geo-Blocking Configuration</h1>
          <p className="text-gray-600 mt-1">Monitor and test geo-blocking functionality</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Configuration Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Current Configuration</h2>
            {config?.enabled ? (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                ENABLED
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
                DISABLED
              </span>
            )}
          </div>

          <div className="space-y-4">
            {/* Implementation Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Implementation Details</p>
                  <p className="text-sm text-blue-700 mt-1">{config?.note}</p>
                  <p className="text-xs text-blue-600 mt-2">
                    <strong>Implementation:</strong> {config?.implementation} | 
                    <strong> Last Updated:</strong> {config?.lastUpdated ? new Date(config.lastUpdated).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Allowed Countries */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Allowed Countries</h3>
              <div className="flex flex-wrap gap-2">
                {config?.allowedCountries.map((code) => (
                  <div
                    key={code}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <span className="text-2xl">{getFlagEmoji(code)}</span>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900">{code}</div>
                      <div className="text-xs text-gray-600">{countryNames[code] || code}</div>
                    </div>
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Info */}
            <div className="text-sm text-gray-600">
              <p><strong>Status:</strong> {config?.enabled ? 'Active - Blocking non-allowed countries' : 'Disabled - All countries allowed'}</p>
              <p className="mt-1"><strong>Method:</strong> IP-based geolocation using geoip-lite database</p>
              <p className="mt-1"><strong>Behavior:</strong> Fail open (allows traffic if country cannot be determined)</p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistics (Last 7 Days)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="text-sm font-medium text-red-700">Blocked Requests</div>
              <div className="text-3xl font-bold text-red-900 mt-2">{stats?.totalBlocked.toLocaleString() || 0}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="text-sm font-medium text-green-700">Allowed Requests</div>
              <div className="text-3xl font-bold text-green-900 mt-2">{stats?.totalAllowed.toLocaleString() || 0}</div>
            </div>
          </div>

          {/* Blocked by Country */}
          {stats && Object.keys(stats.blockedByCountry).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Blocked by Country</h3>
              <div className="space-y-2">
                {Object.entries(stats.blockedByCountry)
                  .sort(([, a], [, b]) => b - a)
                  .map(([country, count]) => (
                    <div key={country} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFlagEmoji(country)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{countryNames[country] || country}</div>
                          <div className="text-xs text-gray-500">{country}</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()} requests</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Recent Blocks */}
          {stats && stats.recentBlocks.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Blocked Requests (Last 24 Hours)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Time</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Country</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">IP Address</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Path</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stats.recentBlocks.slice(0, 10).map((block, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">
                          {new Date(block.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span>{getFlagEmoji(block.country)}</span>
                            <span className="text-gray-900">{block.country}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-700 font-mono text-xs">{block.ip}</td>
                        <td className="px-4 py-2 text-gray-700 truncate max-w-xs">{block.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {stats.recentBlocks.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No blocked requests in the last 24 hours
                </div>
              )}
            </div>
          )}

          {stats && stats.recentBlocks.length === 0 && Object.keys(stats.blockedByCountry).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No geo-blocking events recorded
            </div>
          )}
        </div>

        {/* IP Test Tool */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test IP Address</h2>
          <p className="text-sm text-gray-600 mb-4">
            Enter an IP address to see what country it resolves to and whether it would be blocked.
          </p>

          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={testIP}
              onChange={(e) => setTestIP(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTestIP()}
              placeholder="Enter IP address (e.g., 8.8.8.8)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            <button
              onClick={handleTestIP}
              disabled={testing || !testIP.trim()}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {testing ? 'Testing...' : 'Test IP'}
            </button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`rounded-lg p-4 border ${
              testResult.blocked 
                ? 'bg-red-50 border-red-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                {testResult.blocked ? (
                  <svg className="w-6 h-6 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{testResult.country ? getFlagEmoji(testResult.country) : '🌐'}</span>
                    <span className={`text-lg font-semibold ${testResult.blocked ? 'text-red-900' : 'text-green-900'}`}>
                      {testResult.country ? countryNames[testResult.country] || testResult.country : 'Unknown'}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className={testResult.blocked ? 'text-red-800' : 'text-green-800'}>
                      <strong>Status:</strong> {testResult.message}
                    </p>
                    <p className="text-gray-700"><strong>IP:</strong> {testResult.ip}</p>
                    {testResult.country && <p className="text-gray-700"><strong>Country Code:</strong> {testResult.country}</p>}
                    {testResult.city && <p className="text-gray-700"><strong>City:</strong> {testResult.city}</p>}
                    {testResult.region && <p className="text-gray-700"><strong>Region:</strong> {testResult.region}</p>}
                    {testResult.timezone && <p className="text-gray-700"><strong>Timezone:</strong> {testResult.timezone}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Example IPs */}
          <div className="mt-4 text-xs text-gray-500">
            <p className="font-medium mb-1">Example IPs to test:</p>
            <ul className="space-y-0.5 ml-4">
              <li><button onClick={() => setTestIP('8.8.8.8')} className="text-teal-600 hover:underline">8.8.8.8</button> - United States</li>
              <li><button onClick={() => setTestIP('1.1.1.1')} className="text-teal-600 hover:underline">1.1.1.1</button> - Australia</li>
              <li><button onClick={() => setTestIP('185.220.101.1')} className="text-teal-600 hover:underline">185.220.101.1</button> - Germany (blocked)</li>
              <li><button onClick={() => setTestIP('37.228.133.1')} className="text-teal-600 hover:underline">37.228.133.1</button> - Russia (blocked)</li>
            </ul>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Configuration Management</p>
              <p className="text-sm text-amber-800 mt-1">
                To modify allowed countries, update the <code className="bg-amber-100 px-1 rounded">ALLOWED_COUNTRIES</code> array 
                in <code className="bg-amber-100 px-1 rounded">server.js</code> and redeploy. 
                To enable/disable geo-blocking, set <code className="bg-amber-100 px-1 rounded">GEO_BLOCKING_ENABLED</code> 
                in terraform configuration.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// Helper to get flag emoji from country code
function getFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '🌐';
  
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  
  return String.fromCodePoint(...codePoints);
}

export default AdminGeoBlocking;
